import { fg } from '@atlaskit/platform-feature-flags/fg';

import { AIAnswerQueryStreamType } from '../ai-answer-dialog/ai-answer-query-stream-type';
import { NLPSearchErrorState } from '../ai-answer-dialog/nlp-search-error-state';
import { NLPSearchResultFormat } from '../ai-answer-dialog/nlp-search-result-format';
import type {
	AIAnswerQueryStreamEarlyStop,
	AIAnswerQueryStreamEntry,
	AIAnswerQueryStreamMarkdownFinalPart,
	NLPSearchResultSource,
	NLPSearchType,
} from '../ai-answer-dialog/types';
import { logger } from '../common/utils/logger';
import { getPeopleDataProviders } from '../common/utils/people-data-providers';
import { SAINFeatureControl } from '../common/utils/sain-feature-control';
import { cleanSAINAnswerCitations } from './clean-sain-answer-citations';
import { mapDefinitionMessageToNLPSearchType } from './map-definition-message-to-nlp-search-type';
import { stripLeadingHorizontalRule } from './strip-leading-horizontal-rule';
import {
	isStreamAnswerPart,
	isStreamDefinitionResponse,
	isStreamDynamicLayoutSubAgent,
	isStreamEarlyStop,
	isStreamError,
	isStreamErrorStateQuerySuggestions,
	isStreamFollowUp,
	isStreamGenerationType,
	isStreamSAINResponse,
	isStreamTrace,
	type AgentError,
	type SAINAgentResponse,
	type StreamDefinitionMessage,
	type StreamError,
	type StreamSAINMessage,
} from './types';

const mapMimeTypeToFormat = (mimeType: string): NLPSearchResultFormat => {
	switch (mimeType) {
		case 'text/markdown':
			return NLPSearchResultFormat.MARKDOWN;
		case 'text/adf':
			return NLPSearchResultFormat.ADF;
		case 'text/json':
			return NLPSearchResultFormat.JSON;
		default:
			return NLPSearchResultFormat.MARKDOWN;
	}
};

type MappedError = {
	errorState: NLPSearchErrorState;
	networkErrorReason?: string;
	networkErrorDetails?: string;
	backendFailureType?: string;
};

const mapErrorTo = (error: StreamError | AgentError, traceId?: string | null): MappedError => {
	const errorMessage =
		'message_template' in error ? error.message_template : error.message.message_template;

	switch (errorMessage) {
		case 'NO_ANSWER_LLM':
			return { errorState: NLPSearchErrorState.NoAnswer };
		case 'NO_ANSWER_PEOPLE':
			return { errorState: NLPSearchErrorState.NoAnswerWhoQuestion };
		case 'NO_ANSWER_KEYWORDS':
			return { errorState: NLPSearchErrorState.NoAnswerKeywords };
		case 'NO_ANSWER_SEARCH_QA_PLUGIN':
			return { errorState: NLPSearchErrorState.NoAnswerSearchResults };
		case 'ACCEPTABLE_USE_VIOLATIONS':
			return { errorState: NLPSearchErrorState.AcceptableUseViolation };
		case 'OPENAI_RATE_LIMIT_USER_ABUSE':
			return { errorState: NLPSearchErrorState.NoAnswerOpenAIRateLimitUserAbuse };
		case 'FEATURE_DISABLED_ON_SITE':
			return { errorState: NLPSearchErrorState.AIDisabled };
		default:
			logger.error({
				feature: 'stream-api',
				element: 'container',
				operation: 'fetch',
				category: 'unknown',
				error,
				functionName: 'mapErrorToSain: NetworkError',
				message: 'Streaming answer request failed',
				errorCode: 'message' in error ? error.message.status_code : undefined,
				requestId: 'metadata' in error ? error.metadata?.request_id : undefined,
				traceId,
			});
			return {
				errorState: NLPSearchErrorState.NetworkError,
				networkErrorReason: 'unmapped_backend_error',
				networkErrorDetails: errorMessage,
				backendFailureType: 'unmapped_template',
			};
	}
};

const identityUserCloudARIPrefix = 'ari:cloud:identity::user/';

// SAIN
export const mapSAINMessageToNLPSearchType: (
	message: SAINAgentResponse,
	requestId?: string | null,
) => NLPSearchType = (message: SAINAgentResponse, requestId?: string | null): NLPSearchType => {
	// Handle cases where message is directly in root or nested under message.message
	const messageContent = message.message;

	// The CSM streaming endpoint puts sources inside SAINMessage (message.message.sources)
	// rather than at the SAINAgentResponse level (message.sources). Fall back to the
	// nested location so citations and the Sources section render correctly for both the APIs.
	const sources = message.sources ?? message.message?.sources;
	const peopleDataProviders = getPeopleDataProviders(
		messageContent.message_metadata?.people_data_providers,
	);

	return {
		...(peopleDataProviders.length > 0 && { peopleDataProviders }),
		nlpResult: fg('kd_sain_extract_footnote')
			? cleanSAINAnswerCitations(messageContent.content)
			: messageContent.content,
		uniqueSources: sources
			? sources.map(
					(source): NLPSearchResultSource => ({
						ari: source.ari,
						id: source.id?.toString(),
						title: source.title,
						product: source.product,
						type: source.type,
						url: source.url,
						lastModified: source.lastModified,
						iconUrl: null,
						spaceName: null,
						spaceUrl: null,
						...(source.connectorInfo &&
							fg('kd_sain_connector_info_analytics') && { connectorInfo: source.connectorInfo }),
					}),
				)
			: [],
		disclaimer: messageContent.message_metadata?.disclaimer ?? null,
		errorState: null,
		format: mapMimeTypeToFormat(messageContent.content_mime_type),
		extraAPIAnalyticsAttributes: {
			message_id: messageContent.id,
			conversational_channel_id: messageContent.conversation_channel_id,
			experience_id: messageContent.experience_id,
			user_id: messageContent.user_ari?.replace(identityUserCloudARIPrefix, '') ?? null,
			userQuery: message.human_message?.content ?? null,
			requestId: fg('kd_fix_sain_mau_instrumentation')
				? (requestId ?? null)
				: (messageContent.message_metadata?.request_id ?? null),
			answerGenerationModel: message.usage_data ?? null,
			...(!fg('kd_consolidate_sources_analytics') && { sources }),
			cacheInfo: messageContent.message_metadata?.global_cache_info ?? null,
			pluginInvocations: messageContent.plugin_invocations,
			rawJson: message,
			...(fg('kd_sain_online_debugger') && {
				minion_outputs: message.message?.minion_outputs,
				traceId: requestId ?? null,
			}),
			...(fg('kd_add_plugins_invoked') &&
				(() => {
					const pluginNames = (messageContent.plugin_invocations ?? [])
						.map((p: { plugin_name?: string | null }) => p.plugin_name)
						.filter(Boolean) as string[];

					const minionOutputs = messageContent.minion_outputs ?? [];
					const minionIds = minionOutputs
						.flat()
						.map((m: { minion_id?: string }) => m.minion_id)
						.filter(Boolean) as string[];

					const pluginsInvoked = [...pluginNames, ...minionIds];
					return pluginsInvoked.length > 0 ? { pluginsInvoked } : {};
				})()),
		},
		authority_ui_metadata: messageContent.authority_ui_metadata ?? null,
	};
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const mapStreamSAINResponse: (
	response: StreamSAINMessage,
	traceId?: string | null,
) =>
	| AIAnswerQueryStreamEntry
	| AIAnswerQueryStreamMarkdownFinalPart['value']
	| AIAnswerQueryStreamEarlyStop['value']
	| {
			type: 'other';
	  }
	| {
			type: 'follow-up';
			queries: string[];
	  }
	| {
			type: 'plugin-invocation';
			pluginName: string;
	  } = (
	response: StreamSAINMessage,
	traceId?: string | null,
):
	| AIAnswerQueryStreamEntry
	| AIAnswerQueryStreamMarkdownFinalPart['value']
	| AIAnswerQueryStreamEarlyStop['value']
	| {
			type: 'other';
	  }
	| {
			type: 'follow-up';
			queries: string[];
	  }
	| {
			type: 'plugin-invocation';
			pluginName: string;
	  } => {
	if (isStreamError(response)) {
		const { errorState, networkErrorReason, networkErrorDetails, backendFailureType } = mapErrorTo(
			response,
			traceId,
		);
		return {
			type: AIAnswerQueryStreamType.FinalResponse,
			message: {
				errorState,
				errorCode: response.message.status_code,
				networkErrorReason,
				networkErrorDetails,
				backendFailureType,
				disclaimer: null,
				format: NLPSearchResultFormat.MARKDOWN,
				uniqueSources: [],
			},
		};
	}

	if (isStreamGenerationType(response) && SAINFeatureControl.canRenderLumina()) {
		return {
			type: AIAnswerQueryStreamType.GenerationType,
			message: {
				answer_generation_type: response.message.answer_generation_type,
			},
		};
	}

	if (isStreamTrace(response)) {
		// When the new user-flow FG is on, also surface the rich research-step
		// fields if the BE included a `message_template` (RESEARCH_STEP_*, etc).
		// Legacy traces are unaffected — they just won't carry `traceData`.
		const includeTraceData =
			(SAINFeatureControl.isSAINUpdatedFlowEnabled() ||
				SAINFeatureControl.isSAINLoadingStateTracesUpdateEnabled()) &&
			!!response.message.message_template;
		return {
			type: AIAnswerQueryStreamType.Trace,
			message: response.message.content,
			...(includeTraceData && {
				traceData: {
					messageTemplate: response.message.message_template,
					title: response.message.title,
					stepId: response.message.stepId,
					roundId: response.message.roundId,
					content: response.message.content,
					toolName: response.message.toolName,
					toolIcon: response.message.toolIcon,
					products: response.message.products,
					sources: response.message.sources ?? undefined,
					url: response.message.url,
					product: response.message.product,
				},
			}),
		};
	}

	if (isStreamAnswerPart(response)) {
		return {
			type: AIAnswerQueryStreamType.AnswerPart,
			message: {
				nlpResult: stripLeadingHorizontalRule(response.message.content),
			},
		};
	}

	if (isStreamSAINResponse(response)) {
		return {
			type: AIAnswerQueryStreamType.FinalResponse,
			message: mapSAINMessageToNLPSearchType(response.message, traceId),
		};
	}

	if (isStreamFollowUp(response)) {
		return {
			type: 'follow-up',
			queries: response.message.follow_up_queries ?? [],
		};
	}

	if (isStreamErrorStateQuerySuggestions(response)) {
		return {
			type: AIAnswerQueryStreamType.ErrorStateQuerySuggestions,
			suggested_queries: response.message.suggested_queries ?? [],
		};
	}

	if (SAINFeatureControl.canRenderLumina()) {
		if (isStreamDynamicLayoutSubAgent(response)) {
			return {
				type: AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage,
				message: response.message,
			};
		}
	}

	if (fg('kd_sain_early_stop_hello_exp_fg') || fg('kd_sain_early_stop_customer_exp_fg')) {
		if (isStreamEarlyStop(response)) {
			const earlyStopSearchResult: NLPSearchType = {
				nlpResult: response.message?.reason ?? '',
				format: null,
				uniqueSources: [],
				errorState: NLPSearchErrorState.EarlyStop,
				disclaimer: null,
			};

			return {
				type: AIAnswerQueryStreamType.EarlyStop,
				message: earlyStopSearchResult,
			};
		}
	}

	if (response.type === 'PLUGIN_INVOCATION' && response.message?.plugin_name) {
		return {
			type: 'plugin-invocation' as const,
			pluginName: response.message.plugin_name as string,
		};
	}

	return {
		type: 'other',
	};
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const mapStreamDefinitionResponse: (
	response: StreamDefinitionMessage,
	traceId?: string | null,
) =>
	| AIAnswerQueryStreamEntry
	| AIAnswerQueryStreamMarkdownFinalPart['value']
	| {
			type: 'other';
	  }
	| {
			type: 'follow-up';
			queries: string[];
	  } = (
	response: StreamDefinitionMessage,
	traceId?: string | null,
):
	| AIAnswerQueryStreamEntry
	| AIAnswerQueryStreamMarkdownFinalPart['value']
	| {
			type: 'other';
	  }
	| {
			type: 'follow-up';
			queries: string[];
	  } => {
	if (isStreamError(response)) {
		const { errorState, networkErrorReason, networkErrorDetails, backendFailureType } = mapErrorTo(
			response,
			traceId,
		);
		return {
			type: AIAnswerQueryStreamType.FinalResponse,
			message: {
				errorState,
				errorCode: response.message.status_code,
				networkErrorReason,
				networkErrorDetails,
				backendFailureType,
				disclaimer: null,
				format: NLPSearchResultFormat.MARKDOWN,
				uniqueSources: [],
			},
		};
	}

	if (isStreamTrace(response)) {
		return {
			type: AIAnswerQueryStreamType.Trace,
			message: response.message.content,
		};
	}

	if (isStreamAnswerPart(response)) {
		return {
			type: AIAnswerQueryStreamType.AnswerPart,
			message: {
				nlpResult: response.message.content,
			},
		};
	}

	if (isStreamDefinitionResponse(response)) {
		return {
			type: AIAnswerQueryStreamType.FinalResponse,
			message: mapDefinitionMessageToNLPSearchType(response.message, traceId),
		};
	}

	if (isStreamFollowUp(response)) {
		return {
			type: 'follow-up',
			queries: response.message.follow_up_queries ?? [],
		};
	}

	return {
		// Don't currently handle other types, such as PLUGIN_INVOCATION
		type: 'other',
	};
};
